/**
 * 67.73 step 2: the far field in play (rendering/farStructures.ts) — the match's structures and the parked wave ships given to
 * the field, each drawn full only while its tier is full (its group hidden past it, with the fog's own rule kept), the
 * lamps' night read from the sun where each stands. Presentation only: reads the state and the drawn meshes, writes neither.
 *
 * Off with `?farfield=0` (every full model at any range, as before — the before of the frame's measure).
 */
import * as THREE from 'three';
import type { GameState, StructureState, PlanetState } from '../types';
import { TEAMS } from '../constants';
import { layoutOfStructure } from '../core/modules';
import { buildingsByFunction, isPlanetBuilding } from '../core/buildingScale';
import { structureFullHealth } from '../core/body';
import { unitPaletteFor } from './unitPalette';
import { STRUCTURE_DAMAGED_AT, type StructureMesh } from './renderer';
import { FarStructures, type FarEntry } from './farStructures';
import { settlementLook } from './moduleLook';

/** The far field is drawn unless the URL says `farfield=0`. */
export function farFieldDrawn(): boolean {
  try { return typeof location === 'undefined' || new URLSearchParams(location.search).get('farfield') !== '0'; }
  catch { return true; }
}

/** A structure's far kind, as renderer.ts createStructureMesh picks its look; null: drawn full at any range, as before. */
export function farKindOf(s: Pick<StructureState, 'type' | 'builder'> & Partial<Pick<StructureState, 'pd' | 'yaw'>>, byFunction: boolean, planet?: Pick<PlanetState, 'id' | 'archetype'>): Pick<FarEntry, 'kind' | 'layout'> | null {
  switch (s.type) {
    case 'pointDefence': return { kind: s.pd?.variant ?? 'cram' };   // 69.9: the C-RAM or the CIWS (69.50 step 2a: or the air-defence site)
    case 'fob': case 'relay': case 'beacon': case 'supply': case 'generator': return { kind: s.type };   // (69.50 step 2b: a site's generator)
    case 'ecotourism': return byFunction ? { kind: 'hq' } : null;
    case 'landingPad': return byFunction ? { kind: 'landingPad' } : null;
    case 'house': {   // (63.14 step 6: a settlement's lot, nobody's — its builder's layout, as it is drawn; 72.5: in its settlement's masonry look)
      const layout = byFunction ? layoutOfStructure(s) : null;
      return layout ? { kind: planet ? `layout:${layout.key}@${settlementLook(planet, s.yaw ?? 0)}` : `layout:${layout.key}`, layout } : null;
    }
  }
  // (the team's buildings sized by function: their builder's layout, as the modular switch draws them, or its footprint today)
  if (!byFunction || !isPlanetBuilding(s.type)) return null;
  const layout = layoutOfStructure(s);
  return layout ? { kind: `layout:${layout.key}`, layout } : null;
}

/** A parked wave ship as the action host draws it: its key (−1 − team), side and root. */
export interface ParkedShipView { key: number; team: number; root: THREE.Object3D }

const _q = new THREE.Quaternion();

export class FarFieldPlay {
  private readonly field: FarStructures;
  private signature = NaN;
  private readonly teamColors = TEAMS.map(t => new THREE.Color(unitPaletteFor(t).body));
  private readonly sun = { centre: new THREE.Vector3(), dir: new THREE.Vector3() };

  constructor(private readonly scene: THREE.Object3D, personWorld: number) {
    this.field = new FarStructures(personWorld);
    scene.add(this.field.group);
  }

  /** 384: the far field's late looks, for the shader warm-up (FarStructures.warmParts). */
  warmParts(): THREE.Object3D[] { return this.field.warmParts(); }

  /**
   * Each frame, after the structures' meshes are synced: the entries (rebuilt only when a structure, its form, its side or a
   * parked ship changes), the fog's hiding, the tiers, then each structure's and ship's full model shown only while full.
   */
  update(state: GameState, meshes: ReadonlyMap<number, StructureMesh>, ships: readonly ParkedShipView[], camera: THREE.PerspectiveCamera, bufferHeight: number, sunDir: THREE.Vector3, t: number): void {
    const byFunction = buildingsByFunction(state.config);
    // (the set's signature: ids, kinds, forms and sides, and the parked ships — a number, no allocation)
    let sig = ships.length * 7919;
    for (const s of state.structures) {
      const k = farKindOf(s, byFunction, state.planets[s.planetIndex]);
      if (!k || !meshes.has(s.id)) continue;
      sig = (Math.imul(sig, 31) + s.id * 17 + formCode(s, state) * 5 + (s.ownerId ?? 0) * 3 + k.kind.length) | 0;
    }
    for (const sh of ships) sig = (Math.imul(sig, 31) + sh.key) | 0;
    if (sig !== this.signature) { this.signature = sig; this.rebuild(state, meshes, ships, byFunction); }
    for (const s of state.structures) {
      const sm = meshes.get(s.id);
      if (sm) this.field.setHidden(s.id, sm.fogShown === false);
    }
    const planet = state.planets[state.structures[0]?.planetIndex ?? 0];
    if (planet) this.sun.centre.set(planet.position.x, planet.position.y, planet.position.z);
    this.sun.dir.copy(sunDir);
    this.field.update(camera, bufferHeight, 0, t, this.sun);
    for (const s of state.structures) {
      const sm = meshes.get(s.id);
      if (!sm || !farKindOf(s, byFunction, state.planets[s.planetIndex])) continue;
      sm.group.visible = sm.fogShown !== false && this.field.isFull(s.id);
    }
    for (const sh of ships) sh.root.visible = this.field.isFull(`ship:${sh.key}`);
  }

  private rebuild(state: GameState, meshes: ReadonlyMap<number, StructureMesh>, ships: readonly ParkedShipView[], byFunction: boolean): void {
    const entries: FarEntry[] = [];
    for (const s of state.structures) {
      const k = farKindOf(s, byFunction, state.planets[s.planetIndex]), sm = meshes.get(s.id);
      if (!k || !sm) continue;
      const owner = state.players.find(p => p.id === s.ownerId);
      // (its place as drawn: the seated group, its model's own turn within it)
      sm.group.updateMatrixWorld();
      const q = sm.group.quaternion.clone().multiply(_q.copy(sm.intactModel.quaternion));
      const f = formCode(s, state);
      entries.push({ id: s.id, ...k, position: sm.group.position.clone(), quaternion: q, team: owner ? this.teamColors[owner.teamIndex % this.teamColors.length] : null,
        form: f === 2 ? 'destroyed' : f === 1 ? 'damaged' : 'whole' });
    }
    for (const sh of ships) {
      entries.push({ id: `ship:${sh.key}`, kind: 'dropship', position: sh.root.position.clone(), quaternion: sh.root.quaternion.clone(), team: this.teamColors[sh.team % this.teamColors.length] });
    }
    this.field.setEntries(entries);
  }

  /** A structure's (or `ship:<key>`'s) tier now. */
  tierOf(id: number | string): ReturnType<FarStructures['tierOf']> { return this.field.tierOf(id); }

  /** The field's tiers and cost now (the frame's note). */
  stats(): ReturnType<FarStructures['stats']> { return this.field.stats(); }

  dispose(): void { this.field.group.removeFromParent(); this.field.dispose(); }
}

/** 0 standing, 1 struck (under STRUCTURE_DAMAGED_AT of its full health: a story HQ's its own, 69.61 step 6), 2 destroyed. */
function formCode(s: Pick<StructureState, 'id' | 'type' | 'health'>, state: Pick<GameState, 'board' | 'config'>): number {
  return s.health <= 0 ? 2 : s.health < STRUCTURE_DAMAGED_AT * structureFullHealth(state, s) ? 1 : 0;
}
